/**
 * Enhanced Sticky Block Builder Alpine.js Component
 *
 * Extended functionality from sticky block builder with additional features from pdp-stickybar:
 * - Advanced positioning (top/bottom, with offsets)
 * - Dual trigger system (scroll distance + element-based triggers)
 * - Device-specific sticky behaviors (desktop/tablet/mobile)
 * - Header-relative positioning with automatic header detection
 * - Scroll-based visibility controls with performance optimizations
 */

// Global function to create enhancedStickyBlockBuilder with settings from store
window.enhancedStickyBlockBuilderSettings = function(id, type = 'section', colorSchema = '', additionalData = '') {
  // Ensure settings store is available
  if (!window.arcthemeSettingsStore) {
    console.warn('Arctheme Settings Store is not available. Falling back to empty settings.');
    return enhancedStickyBlockBuilder.component({}, colorSchema, type);
  }

  // Get settings from the global store
  const settings = window.arcthemeSettingsStore.get(type, id) || {};
  
  // Debug log to verify settings are loaded
  if (window.enableDebugMode) { console.log(`EnhancedStickyBlockBuilder loading settings for ${type}:${id}:`, settings); }

  // Parse additional data if provided
  let parsedAdditionalData = {};
  if (additionalData) {
    try {
      parsedAdditionalData = JSON.parse(additionalData);
    } catch (e) {
      console.warn('Failed to parse additional data for enhancedStickyBlockBuilder:', e);
    }
  }

  // Create component instance
  const componentInstance = {
    ...enhancedStickyBlockBuilder.component(settings, colorSchema, type),
    ...parsedAdditionalData
  };
  
  return componentInstance;
};

const enhancedStickyBlockBuilder = {
    name: 'enhancedStickyBlockBuilder',
    component(settings, colorSchema = '', type = '') {
      // Header height management
      const headerHelper = {
        element: null,
        height: 0,
        selector: settings.header_selector || '',
        observer: null,
        updateTimeout: null,
        initialized: false,
        
        init() {
          this.findAndMeasureHeader();
          this.setupHeaderObserver();
          this.initialized = true;
        },
        
        findAndMeasureHeader() {
          let attempts = 0;
          const maxAttempts = 10;
          
          const tryFindHeader = () => {
            // First try custom selector if provided
            if (this.selector) {
              this.element = document.querySelector(this.selector);
            }
            
            // Fallback to automatic detection
            if (!this.element) {
              const headerSelectors = [
                'header',
                '.header',
                '#header',
                '[data-header]',
                '.site-header',
                '.main-header',
                '.header-wrapper'
              ];
              
              for (const selector of headerSelectors) {
                this.element = document.querySelector(selector);
                if (this.element) break;
              }
            }
            
            if (this.element) {
              const computedStyle = window.getComputedStyle(this.element);
              const height = this.element.offsetHeight;
              
              if (height > 0 && computedStyle.display !== 'none') {
                this.height = height;
                document.documentElement.style.setProperty('--header-height', `${height}px`);
                return true;
              }
            }
            
            // Retry if header not found or has no height
            attempts++;
            if (attempts < maxAttempts) {
              setTimeout(tryFindHeader, 200);
            } else {
              // Final fallback
              this.height = 80;
              document.documentElement.style.setProperty('--header-height', '80px');
              console.warn('Enhanced Sticky: Header element not found, using default height of 80px');
            }
            
            return false;
          };
          
          tryFindHeader();
        },
        
        setupHeaderObserver() {
          if ('MutationObserver' in window) {
            this.observer = new MutationObserver(() => {
              clearTimeout(this.updateTimeout);
              this.updateTimeout = setTimeout(() => {
                this.findAndMeasureHeader();
              }, 100);
            });
            
            this.observer.observe(document.body, {
              childList: true,
              subtree: true,
              attributes: true,
              attributeFilter: ['class', 'style']
            });
          }
        },
        
        cleanup() {
          if (this.observer) {
            this.observer.disconnect();
          }
          if (this.updateTimeout) {
            clearTimeout(this.updateTimeout);
          }
        }
      };

      // Trigger element management
      const triggerHelper = {
        element: null,
        selector: settings.trigger_element_selector || '',
        observer: null,
        domObserver: null,
        elementVisible: true, // tracks whether trigger element is in viewport

        init(onVisibilityChange) {
          if (!settings.use_element_trigger || !this.selector) return;

          const attach = () => {
            this.element = document.querySelector(this.selector);
            if (!this.element) return false;

            if (this.domObserver) {
              this.domObserver.disconnect();
              this.domObserver = null;
            }

            if ('IntersectionObserver' in window) {
              if (this.observer) this.observer.disconnect();
              this.observer = new IntersectionObserver((entries) => {
                for (const entry of entries) {
                  this.elementVisible = entry.isIntersecting;
                  if (onVisibilityChange) onVisibilityChange();
                }
              }, { threshold: 0 });
              this.observer.observe(this.element);
            }

            return true;
          };

          if (!attach()) {
            // Watch DOM for the element appearing later (e.g. rendered by JS)
            if ('MutationObserver' in window) {
              this.domObserver = new MutationObserver(() => {
                if (attach()) this.domObserver.disconnect();
              });
              this.domObserver.observe(document.body, { childList: true, subtree: true });
            }
          }
        },

        shouldShow() {
          if (!this.element) return false;

          if ('IntersectionObserver' in window) {
            return !this.elementVisible;
          }

          // Fallback: show when element bottom has scrolled above viewport top
          return this.element.getBoundingClientRect().bottom < 0;
        },

        cleanup() {
          if (this.observer) { this.observer.disconnect(); this.observer = null; }
          if (this.domObserver) { this.domObserver.disconnect(); this.domObserver = null; }
        }
      };

      return {
        // Component state
        isVisible: false,
        isMobile: false,
        isTablet: false,
        isDesktop: false,
        
        // Settings
        triggerOffset: parseInt(settings.trigger_offset) || 300,
        triggerOffsetMobile: parseInt(settings.trigger_offset_mobile) || parseInt(settings.trigger_offset) || 300,
        alwaysShowDesktop: settings.always_show_desktop === true,
        alwaysShowMobile: settings.always_show_mobile === true,
        stickToHeaderDesktop: settings.stick_to_header_desktop === true,
        stickToHeaderTablet: settings.stick_to_header_tablet === true,
        stickToHeaderMobile: settings.stick_to_header_mobile === true,
        useElementTrigger: settings.use_element_trigger === true,
        
        // Computed properties
        get shouldStickToHeader() {
          if (this.isMobile) return this.stickToHeaderMobile;
          if (this.isTablet) return this.stickToHeaderTablet;
          return this.stickToHeaderDesktop;
        },
        
        get currentTriggerOffset() {
          return this.isMobile ? this.triggerOffsetMobile : this.triggerOffset;
        },
        
        get shouldAlwaysShow() {
          return this.isMobile ? this.alwaysShowMobile : this.alwaysShowDesktop;
        },
        
        // Event handlers
        checkVisibility: null,
        handleResize: null,
        scrollTicking: false,
        resizeTicking: false,
        
        init() {
          // Initialize device detection
          this.checkMobileBreakpoint();
          
          // Initialize header helper if needed
          if (this.stickToHeaderDesktop || this.stickToHeaderTablet || this.stickToHeaderMobile) {
            headerHelper.init();
          }
          
          // Initialize trigger element — pass callback so IntersectionObserver
          // can trigger visibility update immediately, not just on next scroll
          triggerHelper.init(() => {
            if (this.checkVisibility) this.checkVisibility();
          });
          
          // Setup event listeners
          this.setupScrollListener();
          this.setupResizeListener();
          
          // Initial visibility check
          if (this.checkVisibility) {
            this.checkVisibility();
          }
        },
        
        destroy() {
          headerHelper.cleanup();
          triggerHelper.cleanup();

          if (this.scrollThrottle) {
            window.removeEventListener('scroll', this.scrollThrottle);
          }

          if (this.resizeThrottle) {
            window.removeEventListener('resize', this.resizeThrottle);
          }
        },
        
        checkMobileBreakpoint() {
          const wasMobile = this.isMobile;
          
          // Get breakpoints from theme settings or use defaults
          const mobileBreakpoint = parseInt(window.themeSettings?.mobile_breakpoint) || 768;
          const tabletBreakpoint = parseInt(window.themeSettings?.tablet_breakpoint) || 1024;
          
          this.isMobile = window.innerWidth <= mobileBreakpoint;
          this.isTablet = window.innerWidth > mobileBreakpoint && window.innerWidth <= tabletBreakpoint;
          this.isDesktop = window.innerWidth > tabletBreakpoint;
          
          // If mobile state changed, recheck visibility
          if (wasMobile !== this.isMobile && this.checkVisibility) {
            this.checkVisibility();
          }
        },
        
        setupScrollListener() {
          this.checkVisibility = () => {
            const scrollY = window.scrollY;
            let shouldShow = this.shouldAlwaysShow;

            if (!shouldShow) {
              if (this.useElementTrigger) {
                if (triggerHelper.element) {
                  shouldShow = triggerHelper.shouldShow();
                } else {
                  // Element not yet in DOM — fall back to scroll distance
                  shouldShow = scrollY > this.currentTriggerOffset;
                }
              } else {
                shouldShow = scrollY > this.currentTriggerOffset;
              }
            }

            if (shouldShow !== this.isVisible) {
              this.isVisible = shouldShow;
            }

            this.scrollTicking = false;
          };
          
          this.scrollThrottle = () => {
            if (!this.scrollTicking) {
              requestAnimationFrame(this.checkVisibility);
              this.scrollTicking = true;
            }
          };
          
          window.addEventListener('scroll', this.scrollThrottle);
          
          // Initial check
          this.checkVisibility();
        },
        
        setupResizeListener() {
          this.handleResize = () => {
            this.checkMobileBreakpoint();
            
            // Update header height if sticking to header
            if ((this.stickToHeaderDesktop || this.stickToHeaderTablet || this.stickToHeaderMobile) && headerHelper.initialized) {
              requestAnimationFrame(() => {
                headerHelper.findAndMeasureHeader();
              });
            }
            
            // Recheck visibility
            if (this.checkVisibility) {
              this.checkVisibility();
            }
            
            this.resizeTicking = false;
          };
          
          this.resizeThrottle = () => {
            if (!this.resizeTicking) {
              requestAnimationFrame(this.handleResize);
              this.resizeTicking = true;
            }
          };
          
          window.addEventListener('resize', this.resizeThrottle);
        },
        
        // Interaction event handling (inherited from blockBuilder)
        handleInteractionEvent(event) {
          if (!event || !event.detail) return;
          
          const eventId = event.detail.eventId || event.detail.id;
          const eventType = event.detail.type || 'toggle';
          
          if (settings.target_event_ids && settings.target_event_ids.includes(eventId)) {
            if (eventType === 'show') {
              this.isVisible = true;
            } else if (eventType === 'hide') {
              this.isVisible = false;
            } else {
              this.isVisible = !this.isVisible;
            }
          }
        }
      };
    }
};

// Register the component globally
if (typeof window !== 'undefined') {
  window.enhancedStickyBlockBuilder = enhancedStickyBlockBuilder.component;
}

export default enhancedStickyBlockBuilder;